// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.11.6
//   protoc               v7.34.0
// source: engine.proto

/* eslint-disable */
import { BinaryReader, BinaryWriter } from "@bufbuild/protobuf/wire";

export const protobufPackage = "engine";

/**
 * A schedule-generation objective. The engine optimizes for the enabled
 * objectives in list order (index 0 = highest priority) via a quantized
 * lexicographic comparator. Mirrors the shared TS model
 * (`@uoplan/core` optimizationPriorities.ts).
 */
export enum OptimizationKind {
  OPTIMIZATION_KIND_UNSPECIFIED = 0,
  OPTIMIZATION_KIND_FREE_DAYS = 1,
  OPTIMIZATION_KIND_GOOD_BREAKS = 2,
  OPTIMIZATION_KIND_COMPACT = 3,
  OPTIMIZATION_KIND_PREFER_EASIER = 4,
  OPTIMIZATION_KIND_PREFER_SENTIMENT = 5,
  OPTIMIZATION_KIND_PREFER_PROFESSOR_RATING = 6,
  UNRECOGNIZED = -1,
}

/** proto3 maps cannot have `repeated` values, so wrap string lists. */
export interface StringList {
  values: string[];
}

export interface BlockedTime {
  /** 0 = Monday ... 6 = Sunday */
  day: number;
  startMinutes: number;
  endMinutes: number;
}

export interface GenerationConstraints {
  minStartMinutes: number;
  maxEndMinutes: number;
  /** Max credits from 1000-level courses allowed (48 - already completed). */
  maxFirstYearCredits?: number | undefined;
  blockedTimes: BlockedTime[];
}

export interface OptimizationPriority {
  kind: OptimizationKind;
  enabled: boolean;
  /** `good_breaks` only: aim for ~`break_count` gaps of ~`break_target_minutes`. */
  breakCount: number;
  breakTargetMinutes: number;
}

export interface DisciplineLevel {
  discipline: string;
  levels: number[];
}

/** Mirror of the TS RemainingRequirement (advanced mode input). */
export interface RemainingRequirement {
  requirementId: string;
  type: string;
  title?: string | undefined;
  candidateCourses: string[];
  creditsNeeded?: number | undefined;
  pickedCount?: number | undefined;
  satisfiedBy: string[];
}

/** Mirror of the TS RequirementWithStatus tree node (advanced mode input). */
export interface RequirementWithStatus {
  type: string;
  title?: string | undefined;
  code?: string | undefined;
  credits?: number | undefined;
  disciplineLevels: DisciplineLevel[];
  excludedDisciplines: string[];
  faculty?: string | undefined;
  options: RequirementWithStatus[];
  complete: boolean;
  satisfiedBy: string[];
  satisfiedOptionIndex?: number | undefined;
  requirementId?: string | undefined;
  candidateCourses: string[];
  creditsNeeded?: number | undefined;
  pickedCount?: number | undefined;
}

/**
 * Per-school credit conventions. Absent/zero fields fall back to uOttawa's
 * values so existing requests keep working unchanged.
 */
export interface CreditConfig {
  /** 0 → 3.0 */
  typicalCourseCredits: number;
  /** 0 → 3.0 */
  defaultCourseCredits: number;
}

export interface GenerationRequest {
  /** --- Basket selection (no degree program active) --- */
  basicPinnedCourses: string[];
  additionalElectivesCount: number;
  basicExcludedCategories: string[];
  /** --- Shared selection / context --- */
  completedCourses: string[];
  studentPrograms: string[];
  programTitle?:
    | string
    | undefined;
  /** --- Program selection (requirement tree computed in TS) --- */
  remainingRequirements: RemainingRequirement[];
  requirementTree: RequirementWithStatus[];
  selectedPerRequirement: { [key: string]: StringList };
  selectedOptionsPerRequirement: { [key: string]: number };
  constrainedPerRequirement: { [key: string]: StringList };
  requirementPriorities: { [key: string]: number };
  coursesThisSemester: number;
  prereqEligibleCourses: string[];
  forcedCourses: string[];
  /** --- Filters / preferences --- */
  levelBuckets: string[];
  languageBuckets: string[];
  electiveLevelBuckets: number[];
  includeClosedComponents: boolean;
  virtualSectionsOnly: boolean;
  frenchImmersionStream: boolean;
  blacklistedCourses: string[];
  /** --- Constraints --- */
  constraints?:
    | GenerationConstraints
    | undefined;
  /** --- Seeds --- */
  currentSeed: number;
  firstSeed: number;
  /** Term id of the schedule dataset to generate against. */
  termId?:
    | number
    | undefined;
  /**
   * Normalized professor name -> RateMyProfessors rating (only rated profs).
   * Used by the `prefer_professor_rating` objective's section weighting.
   */
  professorRatings: { [key: string]: number };
  /** Course code -> A+ percentage, used by the `prefer_easier` objective. */
  courseAplus: { [key: string]: number };
  /**
   * Course code -> overall feedback sentiment (1-5). Used by the
   * `prefer_sentiment` objective's soft selection weighting.
   */
  courseSentiment: { [key: string]: number };
  /**
   * Ordered optimization objectives (index 0 = highest priority). The single
   * source of truth for which soft objectives are active and how they rank;
   * replaces the old per-preference booleans and `compressed_schedule`.
   */
  optimizationPriorities: OptimizationPriority[];
  creditConfig?: CreditConfig | undefined;
}

export interface GenerationRequest_SelectedPerRequirementEntry {
  key: string;
  value?: StringList | undefined;
}

export interface GenerationRequest_SelectedOptionsPerRequirementEntry {
  key: string;
  value: number;
}

export interface GenerationRequest_ConstrainedPerRequirementEntry {
  key: string;
  value?: StringList | undefined;
}

export interface GenerationRequest_RequirementPrioritiesEntry {
  key: string;
  value: number;
}

export interface GenerationRequest_ProfessorRatingsEntry {
  key: string;
  value: number;
}

export interface GenerationRequest_CourseAplusEntry {
  key: string;
  value: number;
}

export interface GenerationRequest_CourseSentimentEntry {
  key: string;
  value: number;
}

/**
 * Re-timetable a FIXED set of courses (the UI swap feature): given an exact set
 * of course codes, find one seeded conflict-free arrangement, or report failure.
 */
export interface TimetableRequest {
  courseCodes: string[];
  constraints?: GenerationConstraints | undefined;
  seed: number;
  includeClosedComponents: boolean;
  virtualSectionsOnly: boolean;
  /** Courses exempt from the virtual-only filter (e.g. pinned courses keep all times). */
  virtualExemptCourses: string[];
  professorRatings: { [key: string]: number };
  applyBlacklist: boolean;
  blacklistedCourses: string[];
  /**
   * Ordered optimization objectives that apply to re-timetabling a fixed course
   * set (timetable-shape + professor-rating objectives; selection objectives are
   * ignored here since the course set is fixed). See GenerationRequest.
   */
  optimizationPriorities: OptimizationPriority[];
  /**
   * Per-school credit conventions — same semantics as GenerationRequest.credit_config.
   * Absent/zero fields fall back to uOttawa's values.
   */
  creditConfig?: CreditConfig | undefined;
}

export interface TimetableRequest_ProfessorRatingsEntry {
  key: string;
  value: number;
}

/**
 * A component -> section choice for a chosen course. The TS side rebuilds the
 * full `CourseEnrollment` (sections + times) from its `DataCache`.
 */
export interface ComponentChoice {
  component: string;
  section: string;
}

export interface ChosenCourse {
  courseCode: string;
  components: ComponentChoice[];
}

export interface EmptyPool {
  label: string;
  requirementId?: string | undefined;
  candidateCourses: string[];
}

export interface PoolDiagnostics {
  emptyPools: EmptyPool[];
  totalAvailable: number;
  totalNeeded: number;
}

export interface GenerationResponse {
  /** True when a conflict-free schedule was produced. */
  hasSchedule: boolean;
  courses: ChosenCourse[];
  /** Swap pool / remaining optional candidates (basic + advanced). */
  optionalPool: string[];
  pinned: string[];
  chosenCourseToRequirement: { [key: string]: string };
  poolDiagnostics?:
    | PoolDiagnostics
    | undefined;
  /** Non-empty when generation failed for a structured reason. */
  error?: string | undefined;
}

export interface GenerationResponse_ChosenCourseToRequirementEntry {
  key: string;
  value: string;
}

function createBaseStringList(): StringList {
  return { values: [] };
}

export const StringList: MessageFns<StringList> = {
  encode(message: StringList, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    for (const v of message.values) {
      writer.uint32(10).string(v!);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): StringList {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseStringList();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.values.push(reader.string());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseBlockedTime(): BlockedTime {
  return { day: 0, startMinutes: 0, endMinutes: 0 };
}

export const BlockedTime: MessageFns<BlockedTime> = {
  encode(message: BlockedTime, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.day !== 0) {
      writer.uint32(8).uint32(message.day);
    }
    if (message.startMinutes !== 0) {
      writer.uint32(16).uint32(message.startMinutes);
    }
    if (message.endMinutes !== 0) {
      writer.uint32(24).uint32(message.endMinutes);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): BlockedTime {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseBlockedTime();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.day = reader.uint32();
          continue;
        }
        case 2: {
          if (tag !== 16) {
            break;
          }

          message.startMinutes = reader.uint32();
          continue;
        }
        case 3: {
          if (tag !== 24) {
            break;
          }

          message.endMinutes = reader.uint32();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationConstraints(): GenerationConstraints {
  return { minStartMinutes: 0, maxEndMinutes: 0, maxFirstYearCredits: undefined, blockedTimes: [] };
}

export const GenerationConstraints: MessageFns<GenerationConstraints> = {
  encode(message: GenerationConstraints, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.minStartMinutes !== 0) {
      writer.uint32(8).uint32(message.minStartMinutes);
    }
    if (message.maxEndMinutes !== 0) {
      writer.uint32(16).uint32(message.maxEndMinutes);
    }
    if (message.maxFirstYearCredits !== undefined) {
      writer.uint32(25).double(message.maxFirstYearCredits);
    }
    for (const v of message.blockedTimes) {
      BlockedTime.encode(v!, writer.uint32(42).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationConstraints {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationConstraints();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.minStartMinutes = reader.uint32();
          continue;
        }
        case 2: {
          if (tag !== 16) {
            break;
          }

          message.maxEndMinutes = reader.uint32();
          continue;
        }
        case 3: {
          if (tag !== 25) {
            break;
          }

          message.maxFirstYearCredits = reader.double();
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.blockedTimes.push(BlockedTime.decode(reader, reader.uint32()));
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseOptimizationPriority(): OptimizationPriority {
  return { kind: 0, enabled: false, breakCount: 0, breakTargetMinutes: 0 };
}

export const OptimizationPriority: MessageFns<OptimizationPriority> = {
  encode(message: OptimizationPriority, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.kind !== 0) {
      writer.uint32(8).int32(message.kind);
    }
    if (message.enabled !== false) {
      writer.uint32(16).bool(message.enabled);
    }
    if (message.breakCount !== 0) {
      writer.uint32(24).uint32(message.breakCount);
    }
    if (message.breakTargetMinutes !== 0) {
      writer.uint32(32).uint32(message.breakTargetMinutes);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): OptimizationPriority {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOptimizationPriority();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.kind = reader.int32() as any;
          continue;
        }
        case 2: {
          if (tag !== 16) {
            break;
          }

          message.enabled = reader.bool();
          continue;
        }
        case 3: {
          if (tag !== 24) {
            break;
          }

          message.breakCount = reader.uint32();
          continue;
        }
        case 4: {
          if (tag !== 32) {
            break;
          }

          message.breakTargetMinutes = reader.uint32();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseDisciplineLevel(): DisciplineLevel {
  return { discipline: "", levels: [] };
}

export const DisciplineLevel: MessageFns<DisciplineLevel> = {
  encode(message: DisciplineLevel, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.discipline !== "") {
      writer.uint32(10).string(message.discipline);
    }
    writer.uint32(18).fork();
    for (const v of message.levels) {
      writer.uint32(v);
    }
    writer.join();
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): DisciplineLevel {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseDisciplineLevel();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.discipline = reader.string();
          continue;
        }
        case 2: {
          if (tag === 16) {
            message.levels.push(reader.uint32());

            continue;
          }

          if (tag === 18) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.levels.push(reader.uint32());
            }

            continue;
          }

          break;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseRemainingRequirement(): RemainingRequirement {
  return {
    requirementId: "",
    type: "",
    title: undefined,
    candidateCourses: [],
    creditsNeeded: undefined,
    pickedCount: undefined,
    satisfiedBy: [],
  };
}

export const RemainingRequirement: MessageFns<RemainingRequirement> = {
  encode(message: RemainingRequirement, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.requirementId !== "") {
      writer.uint32(10).string(message.requirementId);
    }
    if (message.type !== "") {
      writer.uint32(18).string(message.type);
    }
    if (message.title !== undefined) {
      writer.uint32(26).string(message.title);
    }
    for (const v of message.candidateCourses) {
      writer.uint32(34).string(v!);
    }
    if (message.creditsNeeded !== undefined) {
      writer.uint32(41).double(message.creditsNeeded);
    }
    if (message.pickedCount !== undefined) {
      writer.uint32(48).uint32(message.pickedCount);
    }
    for (const v of message.satisfiedBy) {
      writer.uint32(58).string(v!);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): RemainingRequirement {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRemainingRequirement();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.requirementId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.type = reader.string();
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.title = reader.string();
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.candidateCourses.push(reader.string());
          continue;
        }
        case 5: {
          if (tag !== 41) {
            break;
          }

          message.creditsNeeded = reader.double();
          continue;
        }
        case 6: {
          if (tag !== 48) {
            break;
          }

          message.pickedCount = reader.uint32();
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.satisfiedBy.push(reader.string());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseRequirementWithStatus(): RequirementWithStatus {
  return {
    type: "",
    title: undefined,
    code: undefined,
    credits: undefined,
    disciplineLevels: [],
    excludedDisciplines: [],
    faculty: undefined,
    options: [],
    complete: false,
    satisfiedBy: [],
    satisfiedOptionIndex: undefined,
    requirementId: undefined,
    candidateCourses: [],
    creditsNeeded: undefined,
    pickedCount: undefined,
  };
}

export const RequirementWithStatus: MessageFns<RequirementWithStatus> = {
  encode(message: RequirementWithStatus, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.type !== "") {
      writer.uint32(10).string(message.type);
    }
    if (message.title !== undefined) {
      writer.uint32(18).string(message.title);
    }
    if (message.code !== undefined) {
      writer.uint32(26).string(message.code);
    }
    if (message.credits !== undefined) {
      writer.uint32(33).double(message.credits);
    }
    for (const v of message.disciplineLevels) {
      DisciplineLevel.encode(v!, writer.uint32(42).fork()).join();
    }
    for (const v of message.excludedDisciplines) {
      writer.uint32(50).string(v!);
    }
    if (message.faculty !== undefined) {
      writer.uint32(58).string(message.faculty);
    }
    for (const v of message.options) {
      RequirementWithStatus.encode(v!, writer.uint32(66).fork()).join();
    }
    if (message.complete !== false) {
      writer.uint32(72).bool(message.complete);
    }
    for (const v of message.satisfiedBy) {
      writer.uint32(82).string(v!);
    }
    if (message.satisfiedOptionIndex !== undefined) {
      writer.uint32(88).uint32(message.satisfiedOptionIndex);
    }
    if (message.requirementId !== undefined) {
      writer.uint32(98).string(message.requirementId);
    }
    for (const v of message.candidateCourses) {
      writer.uint32(106).string(v!);
    }
    if (message.creditsNeeded !== undefined) {
      writer.uint32(113).double(message.creditsNeeded);
    }
    if (message.pickedCount !== undefined) {
      writer.uint32(120).uint32(message.pickedCount);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): RequirementWithStatus {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRequirementWithStatus();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.type = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.title = reader.string();
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.code = reader.string();
          continue;
        }
        case 4: {
          if (tag !== 33) {
            break;
          }

          message.credits = reader.double();
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.disciplineLevels.push(DisciplineLevel.decode(reader, reader.uint32()));
          continue;
        }
        case 6: {
          if (tag !== 50) {
            break;
          }

          message.excludedDisciplines.push(reader.string());
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.faculty = reader.string();
          continue;
        }
        case 8: {
          if (tag !== 66) {
            break;
          }

          message.options.push(RequirementWithStatus.decode(reader, reader.uint32()));
          continue;
        }
        case 9: {
          if (tag !== 72) {
            break;
          }

          message.complete = reader.bool();
          continue;
        }
        case 10: {
          if (tag !== 82) {
            break;
          }

          message.satisfiedBy.push(reader.string());
          continue;
        }
        case 11: {
          if (tag !== 88) {
            break;
          }

          message.satisfiedOptionIndex = reader.uint32();
          continue;
        }
        case 12: {
          if (tag !== 98) {
            break;
          }

          message.requirementId = reader.string();
          continue;
        }
        case 13: {
          if (tag !== 106) {
            break;
          }

          message.candidateCourses.push(reader.string());
          continue;
        }
        case 14: {
          if (tag !== 113) {
            break;
          }

          message.creditsNeeded = reader.double();
          continue;
        }
        case 15: {
          if (tag !== 120) {
            break;
          }

          message.pickedCount = reader.uint32();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseCreditConfig(): CreditConfig {
  return { typicalCourseCredits: 0, defaultCourseCredits: 0 };
}

export const CreditConfig: MessageFns<CreditConfig> = {
  encode(message: CreditConfig, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.typicalCourseCredits !== 0) {
      writer.uint32(9).double(message.typicalCourseCredits);
    }
    if (message.defaultCourseCredits !== 0) {
      writer.uint32(17).double(message.defaultCourseCredits);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): CreditConfig {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseCreditConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 9) {
            break;
          }

          message.typicalCourseCredits = reader.double();
          continue;
        }
        case 2: {
          if (tag !== 17) {
            break;
          }

          message.defaultCourseCredits = reader.double();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationRequest(): GenerationRequest {
  return {
    basicPinnedCourses: [],
    additionalElectivesCount: 0,
    basicExcludedCategories: [],
    completedCourses: [],
    studentPrograms: [],
    programTitle: undefined,
    remainingRequirements: [],
    requirementTree: [],
    selectedPerRequirement: {},
    selectedOptionsPerRequirement: {},
    constrainedPerRequirement: {},
    requirementPriorities: {},
    coursesThisSemester: 0,
    prereqEligibleCourses: [],
    forcedCourses: [],
    levelBuckets: [],
    languageBuckets: [],
    electiveLevelBuckets: [],
    includeClosedComponents: false,
    virtualSectionsOnly: false,
    frenchImmersionStream: false,
    blacklistedCourses: [],
    constraints: undefined,
    currentSeed: 0,
    firstSeed: 0,
    termId: undefined,
    professorRatings: {},
    courseAplus: {},
    courseSentiment: {},
    optimizationPriorities: [],
    creditConfig: undefined,
  };
}

export const GenerationRequest: MessageFns<GenerationRequest> = {
  encode(message: GenerationRequest, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    for (const v of message.basicPinnedCourses) {
      writer.uint32(18).string(v!);
    }
    if (message.additionalElectivesCount !== 0) {
      writer.uint32(24).uint32(message.additionalElectivesCount);
    }
    for (const v of message.basicExcludedCategories) {
      writer.uint32(34).string(v!);
    }
    for (const v of message.completedCourses) {
      writer.uint32(42).string(v!);
    }
    for (const v of message.studentPrograms) {
      writer.uint32(50).string(v!);
    }
    if (message.programTitle !== undefined) {
      writer.uint32(58).string(message.programTitle);
    }
    for (const v of message.remainingRequirements) {
      RemainingRequirement.encode(v!, writer.uint32(66).fork()).join();
    }
    for (const v of message.requirementTree) {
      RequirementWithStatus.encode(v!, writer.uint32(74).fork()).join();
    }
    globalThis.Object.entries(message.selectedPerRequirement).forEach(([key, value]: [string, StringList]) => {
      GenerationRequest_SelectedPerRequirementEntry.encode({ key: key as any, value }, writer.uint32(82).fork()).join();
    });
    globalThis.Object.entries(message.selectedOptionsPerRequirement).forEach(([key, value]: [string, number]) => {
      GenerationRequest_SelectedOptionsPerRequirementEntry.encode({ key: key as any, value }, writer.uint32(90).fork())
        .join();
    });
    globalThis.Object.entries(message.constrainedPerRequirement).forEach(([key, value]: [string, StringList]) => {
      GenerationRequest_ConstrainedPerRequirementEntry.encode({ key: key as any, value }, writer.uint32(98).fork())
        .join();
    });
    globalThis.Object.entries(message.requirementPriorities).forEach(([key, value]: [string, number]) => {
      GenerationRequest_RequirementPrioritiesEntry.encode({ key: key as any, value }, writer.uint32(106).fork()).join();
    });
    if (message.coursesThisSemester !== 0) {
      writer.uint32(112).uint32(message.coursesThisSemester);
    }
    for (const v of message.prereqEligibleCourses) {
      writer.uint32(122).string(v!);
    }
    for (const v of message.forcedCourses) {
      writer.uint32(130).string(v!);
    }
    for (const v of message.levelBuckets) {
      writer.uint32(138).string(v!);
    }
    for (const v of message.languageBuckets) {
      writer.uint32(146).string(v!);
    }
    writer.uint32(154).fork();
    for (const v of message.electiveLevelBuckets) {
      writer.uint32(v);
    }
    writer.join();
    if (message.includeClosedComponents !== false) {
      writer.uint32(160).bool(message.includeClosedComponents);
    }
    if (message.virtualSectionsOnly !== false) {
      writer.uint32(168).bool(message.virtualSectionsOnly);
    }
    if (message.frenchImmersionStream !== false) {
      writer.uint32(184).bool(message.frenchImmersionStream);
    }
    for (const v of message.blacklistedCourses) {
      writer.uint32(194).string(v!);
    }
    if (message.constraints !== undefined) {
      GenerationConstraints.encode(message.constraints, writer.uint32(202).fork()).join();
    }
    if (message.currentSeed !== 0) {
      writer.uint32(208).uint32(message.currentSeed);
    }
    if (message.firstSeed !== 0) {
      writer.uint32(216).uint32(message.firstSeed);
    }
    if (message.termId !== undefined) {
      writer.uint32(224).uint32(message.termId);
    }
    globalThis.Object.entries(message.professorRatings).forEach(([key, value]: [string, number]) => {
      GenerationRequest_ProfessorRatingsEntry.encode({ key: key as any, value }, writer.uint32(234).fork()).join();
    });
    globalThis.Object.entries(message.courseAplus).forEach(([key, value]: [string, number]) => {
      GenerationRequest_CourseAplusEntry.encode({ key: key as any, value }, writer.uint32(242).fork()).join();
    });
    globalThis.Object.entries(message.courseSentiment).forEach(([key, value]: [string, number]) => {
      GenerationRequest_CourseSentimentEntry.encode({ key: key as any, value }, writer.uint32(258).fork()).join();
    });
    for (const v of message.optimizationPriorities) {
      OptimizationPriority.encode(v!, writer.uint32(274).fork()).join();
    }
    if (message.creditConfig !== undefined) {
      CreditConfig.encode(message.creditConfig, writer.uint32(282).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationRequest {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.basicPinnedCourses.push(reader.string());
          continue;
        }
        case 3: {
          if (tag !== 24) {
            break;
          }

          message.additionalElectivesCount = reader.uint32();
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.basicExcludedCategories.push(reader.string());
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.completedCourses.push(reader.string());
          continue;
        }
        case 6: {
          if (tag !== 50) {
            break;
          }

          message.studentPrograms.push(reader.string());
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.programTitle = reader.string();
          continue;
        }
        case 8: {
          if (tag !== 66) {
            break;
          }

          message.remainingRequirements.push(RemainingRequirement.decode(reader, reader.uint32()));
          continue;
        }
        case 9: {
          if (tag !== 74) {
            break;
          }

          message.requirementTree.push(RequirementWithStatus.decode(reader, reader.uint32()));
          continue;
        }
        case 10: {
          if (tag !== 82) {
            break;
          }

          const entry10 = GenerationRequest_SelectedPerRequirementEntry.decode(reader, reader.uint32());
          if (entry10.value !== undefined) {
            message.selectedPerRequirement[entry10.key] = entry10.value;
          }
          continue;
        }
        case 11: {
          if (tag !== 90) {
            break;
          }

          const entry11 = GenerationRequest_SelectedOptionsPerRequirementEntry.decode(reader, reader.uint32());
          if (entry11.value !== undefined) {
            message.selectedOptionsPerRequirement[entry11.key] = entry11.value;
          }
          continue;
        }
        case 12: {
          if (tag !== 98) {
            break;
          }

          const entry12 = GenerationRequest_ConstrainedPerRequirementEntry.decode(reader, reader.uint32());
          if (entry12.value !== undefined) {
            message.constrainedPerRequirement[entry12.key] = entry12.value;
          }
          continue;
        }
        case 13: {
          if (tag !== 106) {
            break;
          }

          const entry13 = GenerationRequest_RequirementPrioritiesEntry.decode(reader, reader.uint32());
          if (entry13.value !== undefined) {
            message.requirementPriorities[entry13.key] = entry13.value;
          }
          continue;
        }
        case 14: {
          if (tag !== 112) {
            break;
          }

          message.coursesThisSemester = reader.uint32();
          continue;
        }
        case 15: {
          if (tag !== 122) {
            break;
          }

          message.prereqEligibleCourses.push(reader.string());
          continue;
        }
        case 16: {
          if (tag !== 130) {
            break;
          }

          message.forcedCourses.push(reader.string());
          continue;
        }
        case 17: {
          if (tag !== 138) {
            break;
          }

          message.levelBuckets.push(reader.string());
          continue;
        }
        case 18: {
          if (tag !== 146) {
            break;
          }

          message.languageBuckets.push(reader.string());
          continue;
        }
        case 19: {
          if (tag === 152) {
            message.electiveLevelBuckets.push(reader.uint32());

            continue;
          }

          if (tag === 154) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.electiveLevelBuckets.push(reader.uint32());
            }

            continue;
          }

          break;
        }
        case 20: {
          if (tag !== 160) {
            break;
          }

          message.includeClosedComponents = reader.bool();
          continue;
        }
        case 21: {
          if (tag !== 168) {
            break;
          }

          message.virtualSectionsOnly = reader.bool();
          continue;
        }
        case 23: {
          if (tag !== 184) {
            break;
          }

          message.frenchImmersionStream = reader.bool();
          continue;
        }
        case 24: {
          if (tag !== 194) {
            break;
          }

          message.blacklistedCourses.push(reader.string());
          continue;
        }
        case 25: {
          if (tag !== 202) {
            break;
          }

          message.constraints = GenerationConstraints.decode(reader, reader.uint32());
          continue;
        }
        case 26: {
          if (tag !== 208) {
            break;
          }

          message.currentSeed = reader.uint32();
          continue;
        }
        case 27: {
          if (tag !== 216) {
            break;
          }

          message.firstSeed = reader.uint32();
          continue;
        }
        case 28: {
          if (tag !== 224) {
            break;
          }

          message.termId = reader.uint32();
          continue;
        }
        case 29: {
          if (tag !== 234) {
            break;
          }

          const entry29 = GenerationRequest_ProfessorRatingsEntry.decode(reader, reader.uint32());
          if (entry29.value !== undefined) {
            message.professorRatings[entry29.key] = entry29.value;
          }
          continue;
        }
        case 30: {
          if (tag !== 242) {
            break;
          }

          const entry30 = GenerationRequest_CourseAplusEntry.decode(reader, reader.uint32());
          if (entry30.value !== undefined) {
            message.courseAplus[entry30.key] = entry30.value;
          }
          continue;
        }
        case 32: {
          if (tag !== 258) {
            break;
          }

          const entry32 = GenerationRequest_CourseSentimentEntry.decode(reader, reader.uint32());
          if (entry32.value !== undefined) {
            message.courseSentiment[entry32.key] = entry32.value;
          }
          continue;
        }
        case 34: {
          if (tag !== 274) {
            break;
          }

          message.optimizationPriorities.push(OptimizationPriority.decode(reader, reader.uint32()));
          continue;
        }
        case 35: {
          if (tag !== 282) {
            break;
          }

          message.creditConfig = CreditConfig.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationRequest_SelectedPerRequirementEntry(): GenerationRequest_SelectedPerRequirementEntry {
  return { key: "", value: undefined };
}

export const GenerationRequest_SelectedPerRequirementEntry: MessageFns<GenerationRequest_SelectedPerRequirementEntry> =
  {
    encode(
      message: GenerationRequest_SelectedPerRequirementEntry,
      writer: BinaryWriter = new BinaryWriter(),
    ): BinaryWriter {
      if (message.key !== "") {
        writer.uint32(10).string(message.key);
      }
      if (message.value !== undefined) {
        StringList.encode(message.value, writer.uint32(18).fork()).join();
      }
      return writer;
    },

    decode(input: BinaryReader | Uint8Array, length?: number): GenerationRequest_SelectedPerRequirementEntry {
      const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
      const end = length === undefined ? reader.len : reader.pos + length;
      const message = createBaseGenerationRequest_SelectedPerRequirementEntry();
      while (reader.pos < end) {
        const tag = reader.uint32();
        switch (tag >>> 3) {
          case 1: {
            if (tag !== 10) {
              break;
            }

            message.key = reader.string();
            continue;
          }
          case 2: {
            if (tag !== 18) {
              break;
            }

            message.value = StringList.decode(reader, reader.uint32());
            continue;
          }
        }
        if ((tag & 7) === 4 || tag === 0) {
          break;
        }
        reader.skip(tag & 7);
      }
      return message;
    },
  };

function createBaseGenerationRequest_SelectedOptionsPerRequirementEntry(): GenerationRequest_SelectedOptionsPerRequirementEntry {
  return { key: "", value: 0 };
}

export const GenerationRequest_SelectedOptionsPerRequirementEntry: MessageFns<
  GenerationRequest_SelectedOptionsPerRequirementEntry
> = {
  encode(
    message: GenerationRequest_SelectedOptionsPerRequirementEntry,
    writer: BinaryWriter = new BinaryWriter(),
  ): BinaryWriter {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== 0) {
      writer.uint32(16).uint32(message.value);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationRequest_SelectedOptionsPerRequirementEntry {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationRequest_SelectedOptionsPerRequirementEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 16) {
            break;
          }

          message.value = reader.uint32();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationRequest_ConstrainedPerRequirementEntry(): GenerationRequest_ConstrainedPerRequirementEntry {
  return { key: "", value: undefined };
}

export const GenerationRequest_ConstrainedPerRequirementEntry: MessageFns<
  GenerationRequest_ConstrainedPerRequirementEntry
> = {
  encode(
    message: GenerationRequest_ConstrainedPerRequirementEntry,
    writer: BinaryWriter = new BinaryWriter(),
  ): BinaryWriter {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== undefined) {
      StringList.encode(message.value, writer.uint32(18).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationRequest_ConstrainedPerRequirementEntry {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationRequest_ConstrainedPerRequirementEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.value = StringList.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationRequest_RequirementPrioritiesEntry(): GenerationRequest_RequirementPrioritiesEntry {
  return { key: "", value: 0 };
}

export const GenerationRequest_RequirementPrioritiesEntry: MessageFns<GenerationRequest_RequirementPrioritiesEntry> = {
  encode(
    message: GenerationRequest_RequirementPrioritiesEntry,
    writer: BinaryWriter = new BinaryWriter(),
  ): BinaryWriter {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== 0) {
      writer.uint32(16).uint32(message.value);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationRequest_RequirementPrioritiesEntry {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationRequest_RequirementPrioritiesEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 16) {
            break;
          }

          message.value = reader.uint32();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationRequest_ProfessorRatingsEntry(): GenerationRequest_ProfessorRatingsEntry {
  return { key: "", value: 0 };
}

export const GenerationRequest_ProfessorRatingsEntry: MessageFns<GenerationRequest_ProfessorRatingsEntry> = {
  encode(message: GenerationRequest_ProfessorRatingsEntry, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== 0) {
      writer.uint32(17).double(message.value);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationRequest_ProfessorRatingsEntry {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationRequest_ProfessorRatingsEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 17) {
            break;
          }

          message.value = reader.double();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationRequest_CourseAplusEntry(): GenerationRequest_CourseAplusEntry {
  return { key: "", value: 0 };
}

export const GenerationRequest_CourseAplusEntry: MessageFns<GenerationRequest_CourseAplusEntry> = {
  encode(message: GenerationRequest_CourseAplusEntry, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== 0) {
      writer.uint32(17).double(message.value);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationRequest_CourseAplusEntry {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationRequest_CourseAplusEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 17) {
            break;
          }

          message.value = reader.double();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationRequest_CourseSentimentEntry(): GenerationRequest_CourseSentimentEntry {
  return { key: "", value: 0 };
}

export const GenerationRequest_CourseSentimentEntry: MessageFns<GenerationRequest_CourseSentimentEntry> = {
  encode(message: GenerationRequest_CourseSentimentEntry, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== 0) {
      writer.uint32(17).double(message.value);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationRequest_CourseSentimentEntry {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationRequest_CourseSentimentEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 17) {
            break;
          }

          message.value = reader.double();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseTimetableRequest(): TimetableRequest {
  return {
    courseCodes: [],
    constraints: undefined,
    seed: 0,
    includeClosedComponents: false,
    virtualSectionsOnly: false,
    virtualExemptCourses: [],
    professorRatings: {},
    applyBlacklist: false,
    blacklistedCourses: [],
    optimizationPriorities: [],
    creditConfig: undefined,
  };
}

export const TimetableRequest: MessageFns<TimetableRequest> = {
  encode(message: TimetableRequest, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    for (const v of message.courseCodes) {
      writer.uint32(10).string(v!);
    }
    if (message.constraints !== undefined) {
      GenerationConstraints.encode(message.constraints, writer.uint32(18).fork()).join();
    }
    if (message.seed !== 0) {
      writer.uint32(24).uint32(message.seed);
    }
    if (message.includeClosedComponents !== false) {
      writer.uint32(32).bool(message.includeClosedComponents);
    }
    if (message.virtualSectionsOnly !== false) {
      writer.uint32(40).bool(message.virtualSectionsOnly);
    }
    for (const v of message.virtualExemptCourses) {
      writer.uint32(50).string(v!);
    }
    globalThis.Object.entries(message.professorRatings).forEach(([key, value]: [string, number]) => {
      TimetableRequest_ProfessorRatingsEntry.encode({ key: key as any, value }, writer.uint32(58).fork()).join();
    });
    if (message.applyBlacklist !== false) {
      writer.uint32(64).bool(message.applyBlacklist);
    }
    for (const v of message.blacklistedCourses) {
      writer.uint32(74).string(v!);
    }
    for (const v of message.optimizationPriorities) {
      OptimizationPriority.encode(v!, writer.uint32(90).fork()).join();
    }
    if (message.creditConfig !== undefined) {
      CreditConfig.encode(message.creditConfig, writer.uint32(98).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): TimetableRequest {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseTimetableRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.courseCodes.push(reader.string());
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.constraints = GenerationConstraints.decode(reader, reader.uint32());
          continue;
        }
        case 3: {
          if (tag !== 24) {
            break;
          }

          message.seed = reader.uint32();
          continue;
        }
        case 4: {
          if (tag !== 32) {
            break;
          }

          message.includeClosedComponents = reader.bool();
          continue;
        }
        case 5: {
          if (tag !== 40) {
            break;
          }

          message.virtualSectionsOnly = reader.bool();
          continue;
        }
        case 6: {
          if (tag !== 50) {
            break;
          }

          message.virtualExemptCourses.push(reader.string());
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          const entry7 = TimetableRequest_ProfessorRatingsEntry.decode(reader, reader.uint32());
          if (entry7.value !== undefined) {
            message.professorRatings[entry7.key] = entry7.value;
          }
          continue;
        }
        case 8: {
          if (tag !== 64) {
            break;
          }

          message.applyBlacklist = reader.bool();
          continue;
        }
        case 9: {
          if (tag !== 74) {
            break;
          }

          message.blacklistedCourses.push(reader.string());
          continue;
        }
        case 11: {
          if (tag !== 90) {
            break;
          }

          message.optimizationPriorities.push(OptimizationPriority.decode(reader, reader.uint32()));
          continue;
        }
        case 12: {
          if (tag !== 98) {
            break;
          }

          message.creditConfig = CreditConfig.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseTimetableRequest_ProfessorRatingsEntry(): TimetableRequest_ProfessorRatingsEntry {
  return { key: "", value: 0 };
}

export const TimetableRequest_ProfessorRatingsEntry: MessageFns<TimetableRequest_ProfessorRatingsEntry> = {
  encode(message: TimetableRequest_ProfessorRatingsEntry, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== 0) {
      writer.uint32(17).double(message.value);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): TimetableRequest_ProfessorRatingsEntry {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseTimetableRequest_ProfessorRatingsEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 17) {
            break;
          }

          message.value = reader.double();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseComponentChoice(): ComponentChoice {
  return { component: "", section: "" };
}

export const ComponentChoice: MessageFns<ComponentChoice> = {
  encode(message: ComponentChoice, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.component !== "") {
      writer.uint32(10).string(message.component);
    }
    if (message.section !== "") {
      writer.uint32(18).string(message.section);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ComponentChoice {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseComponentChoice();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.component = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.section = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseChosenCourse(): ChosenCourse {
  return { courseCode: "", components: [] };
}

export const ChosenCourse: MessageFns<ChosenCourse> = {
  encode(message: ChosenCourse, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.courseCode !== "") {
      writer.uint32(10).string(message.courseCode);
    }
    for (const v of message.components) {
      ComponentChoice.encode(v!, writer.uint32(18).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ChosenCourse {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseChosenCourse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.courseCode = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.components.push(ComponentChoice.decode(reader, reader.uint32()));
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseEmptyPool(): EmptyPool {
  return { label: "", requirementId: undefined, candidateCourses: [] };
}

export const EmptyPool: MessageFns<EmptyPool> = {
  encode(message: EmptyPool, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.label !== "") {
      writer.uint32(10).string(message.label);
    }
    if (message.requirementId !== undefined) {
      writer.uint32(18).string(message.requirementId);
    }
    for (const v of message.candidateCourses) {
      writer.uint32(26).string(v!);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): EmptyPool {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseEmptyPool();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.label = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.requirementId = reader.string();
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.candidateCourses.push(reader.string());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBasePoolDiagnostics(): PoolDiagnostics {
  return { emptyPools: [], totalAvailable: 0, totalNeeded: 0 };
}

export const PoolDiagnostics: MessageFns<PoolDiagnostics> = {
  encode(message: PoolDiagnostics, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    for (const v of message.emptyPools) {
      EmptyPool.encode(v!, writer.uint32(10).fork()).join();
    }
    if (message.totalAvailable !== 0) {
      writer.uint32(16).uint32(message.totalAvailable);
    }
    if (message.totalNeeded !== 0) {
      writer.uint32(24).uint32(message.totalNeeded);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): PoolDiagnostics {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePoolDiagnostics();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.emptyPools.push(EmptyPool.decode(reader, reader.uint32()));
          continue;
        }
        case 2: {
          if (tag !== 16) {
            break;
          }

          message.totalAvailable = reader.uint32();
          continue;
        }
        case 3: {
          if (tag !== 24) {
            break;
          }

          message.totalNeeded = reader.uint32();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationResponse(): GenerationResponse {
  return {
    hasSchedule: false,
    courses: [],
    optionalPool: [],
    pinned: [],
    chosenCourseToRequirement: {},
    poolDiagnostics: undefined,
    error: undefined,
  };
}

export const GenerationResponse: MessageFns<GenerationResponse> = {
  encode(message: GenerationResponse, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.hasSchedule !== false) {
      writer.uint32(8).bool(message.hasSchedule);
    }
    for (const v of message.courses) {
      ChosenCourse.encode(v!, writer.uint32(18).fork()).join();
    }
    for (const v of message.optionalPool) {
      writer.uint32(26).string(v!);
    }
    for (const v of message.pinned) {
      writer.uint32(34).string(v!);
    }
    globalThis.Object.entries(message.chosenCourseToRequirement).forEach(([key, value]: [string, string]) => {
      GenerationResponse_ChosenCourseToRequirementEntry.encode({ key: key as any, value }, writer.uint32(42).fork())
        .join();
    });
    if (message.poolDiagnostics !== undefined) {
      PoolDiagnostics.encode(message.poolDiagnostics, writer.uint32(50).fork()).join();
    }
    if (message.error !== undefined) {
      writer.uint32(58).string(message.error);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationResponse {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.hasSchedule = reader.bool();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.courses.push(ChosenCourse.decode(reader, reader.uint32()));
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.optionalPool.push(reader.string());
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.pinned.push(reader.string());
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          const entry5 = GenerationResponse_ChosenCourseToRequirementEntry.decode(reader, reader.uint32());
          if (entry5.value !== undefined) {
            message.chosenCourseToRequirement[entry5.key] = entry5.value;
          }
          continue;
        }
        case 6: {
          if (tag !== 50) {
            break;
          }

          message.poolDiagnostics = PoolDiagnostics.decode(reader, reader.uint32());
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.error = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

function createBaseGenerationResponse_ChosenCourseToRequirementEntry(): GenerationResponse_ChosenCourseToRequirementEntry {
  return { key: "", value: "" };
}

export const GenerationResponse_ChosenCourseToRequirementEntry: MessageFns<
  GenerationResponse_ChosenCourseToRequirementEntry
> = {
  encode(
    message: GenerationResponse_ChosenCourseToRequirementEntry,
    writer: BinaryWriter = new BinaryWriter(),
  ): BinaryWriter {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== "") {
      writer.uint32(18).string(message.value);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GenerationResponse_ChosenCourseToRequirementEntry {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGenerationResponse_ChosenCourseToRequirementEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.value = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },
};

export interface MessageFns<T> {
  encode(message: T, writer?: BinaryWriter): BinaryWriter;
  decode(input: BinaryReader | Uint8Array, length?: number): T;
}
